import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { FaExternalLinkAlt, FaTimes, FaLock, FaSearch, FaFlask } from "react-icons/fa";
import { ACCENTS, TOOL_CATEGORIES } from "../../utils/Constant";

const TAB_STORAGE_KEY = "lims.tools.activeCategory";

const readStoredTab = () => {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
    return TOOL_CATEGORIES.some((c) => c.id === stored) ? stored : TOOL_CATEGORIES[0].id;
  } catch {
    return TOOL_CATEGORIES[0].id;
  }
};

const Tools = () => {
  const [categoryId, setCategoryId] = useState(readStoredTab);
  const [activeId, setActiveId] = useState(null);
  const [loadedIds, setLoadedIds] = useState({});
  const [query, setQuery] = useState("");
  const previewRef = useRef(null);
  const tabRefs = useRef({});

  const category = TOOL_CATEGORIES.find((c) => c.id === categoryId) || TOOL_CATEGORIES[0];

  const visibleTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return category.tools;
    return category.tools.filter((t) =>
      [t.name, t.task, t.url].some((field) => field.toLowerCase().includes(q))
    );
  }, [category, query]);

  const activeTool = category.tools.find((t) => t.id === activeId) || null;

  useEffect(() => {
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, categoryId);
    } catch {
      // storage unavailable; tab just won't be remembered
    }
  }, [categoryId]);

  // Bring the preview into view on small screens where it sits below a long grid.
  useEffect(() => {
    if (activeId && previewRef.current) {
      previewRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeId]);

  const handleTabChange = useCallback((id) => {
    setCategoryId(id);
    setActiveId(null);
    setQuery("");
  }, []);

  const handleTabKeyDown = useCallback(
    (e, index) => {
      let next = null;
      if (e.key === "ArrowRight") next = (index + 1) % TOOL_CATEGORIES.length;
      if (e.key === "ArrowLeft") next = (index - 1 + TOOL_CATEGORIES.length) % TOOL_CATEGORIES.length;
      if (e.key === "Home") next = 0;
      if (e.key === "End") next = TOOL_CATEGORIES.length - 1;
      if (next === null) return;
      e.preventDefault();
      const nextId = TOOL_CATEGORIES[next].id;
      handleTabChange(nextId);
      tabRefs.current[nextId]?.focus();
    },
    [handleTabChange]
  );

  const handleSelect = useCallback((id) => {
    setActiveId((current) => (current === id ? null : id));
  }, []);

  const handleIframeLoad = useCallback((id) => {
    setLoadedIds((prev) => ({ ...prev, [id]: true }));
  }, []);

  return (
    <div className="min-h-screen w-full text-[#1B2521]">
      <div className="mx-auto max-w-full px-4 py-4 sm:px-6 sm:py-1 lg:px-8">
        {/* Header */}
        <header className="mb-5 border-b border-[#D6D2C4] pb-4 sm:mb-6 sm:pb-6">
          <h1 className="font-serif text-2xl leading-tight text-[#152420] sm:text-4xl lg:text-5xl">
            {category.title}
          </h1>
        </header>

        {/* Category tabs + search */}
        <div className="mb-5 flex flex-col gap-3 sm:mb-6 lg:flex-row lg:items-center lg:justify-between">
          <div
            role="tablist"
            aria-label="Tool categories"
            className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
          >
            {TOOL_CATEGORIES.map((c, index) => {
              const TabIcon = c.icon;
              const selected = c.id === category.id;
              return (
                <button
                  key={c.id}
                  ref={(el) => (tabRefs.current[c.id] = el)}
                  type="button"
                  role="tab"
                  id={`tools-tab-${c.id}`}
                  aria-selected={selected}
                  aria-controls={`tools-panel-${c.id}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => handleTabChange(c.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                  className={[
                    "flex flex-none items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E7F6C] focus-visible:ring-offset-2",
                    selected
                      ? "border-[#1B2521] bg-[#1B2521] text-[#F0EEE6]"
                      : "border-[#D6D2C4] bg-[#FBFAF5] text-[#4A5651] hover:border-[#C9C4B3] hover:text-[#152420]",
                  ].join(" ")}
                >
                  <TabIcon aria-hidden="true" className="h-3.5 w-3.5" />
                  <span>{c.label}</span>
                  <span
                    className={[
                      "rounded-full px-1.5 font-mono text-[10px] leading-4",
                      selected ? "bg-[#3A4A44] text-[#7FCBB2]" : "bg-[#EDEAE0] text-[#7A857E]",
                    ].join(" ")}
                  >
                    {c.tools.length}
                  </span>
                </button>
              );
            })}
          </div>

          {category.tools.length > 0 && (
            <label className="relative block w-full lg:w-72">
              <span className="sr-only">Search {category.label} tools</span>
              <FaSearch
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#7A857E]"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${category.label.toLowerCase()} tools…`}
                className="w-full rounded-full border border-[#D6D2C4] bg-[#FBFAF5] py-2 pl-9 pr-4 text-sm text-[#152420] placeholder:text-[#9AA39D] focus:border-[#1E7F6C] focus:outline-none focus:ring-2 focus:ring-[#1E7F6C]/20"
              />
            </label>
          )}
        </div>

        <div
          role="tabpanel"
          id={`tools-panel-${category.id}`}
          aria-labelledby={`tools-tab-${category.id}`}
        >
          {category.tools.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[#D6D2C4] bg-[#FBFAF5] px-6 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#E3F1EC] text-lg text-[#1E7F6C]">
                <FaFlask aria-hidden="true" />
              </span>
              <p className="font-serif text-lg text-[#152420] sm:text-xl">
                {category.label} tools are coming soon
              </p>
              <p className="max-w-sm text-sm text-[#4A5651]">
                We&apos;re curating tools for this section. Check back shortly.
              </p>
            </div>
          ) : visibleTools.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#D6D2C4] bg-[#FBFAF5] px-6 py-10 text-center">
              <p className="font-serif text-base text-[#152420] sm:text-lg">
                No tools match &ldquo;{query}&rdquo;
              </p>
              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-3 font-mono text-xs text-[#1E7F6C] underline-offset-2 hover:underline sm:text-sm"
              >
                Clear search
              </button>
            </div>
          ) : (
            <section
              aria-label={`${category.label} tools`}
              className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
            >
              {visibleTools.map((tool) => {
                const Icon = tool.icon;
                const isActive = tool.id === activeId;
                const index = category.tools.indexOf(tool);
                const accent = ACCENTS[index % ACCENTS.length];

                return (
                  <div key={tool.id} className="relative">
                    <button
                      type="button"
                      onClick={() => handleSelect(tool.id)}
                      aria-pressed={isActive}
                      className={[
                        "group flex h-full w-full flex-row items-center gap-3 rounded-xl border bg-[#FBFAF5] px-4 py-4 pr-12 text-left shadow-sm transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F0EEE6] min-[420px]:flex-col min-[420px]:items-start min-[420px]:py-5",
                        isActive
                          ? "border-transparent shadow-md"
                          : "border-[#E1DDD0] hover:-translate-y-0.5 hover:border-[#C9C4B3] hover:shadow-md",
                      ].join(" ")}
                      style={isActive ? { boxShadow: `0 0 0 2px ${accent.text}` } : undefined}
                    >
                      <span
                        className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-lg"
                        style={{ backgroundColor: accent.bg, color: accent.text }}
                      >
                        <Icon aria-hidden="true" />
                      </span>

                      <span className="flex min-w-0 flex-col">
                        <span className="font-serif text-base leading-snug text-[#152420] sm:text-lg">
                          {tool.name}
                        </span>
                        <span className="mt-0.5 font-mono text-[11px] leading-snug text-[#7A857E] sm:text-xs">
                          {tool.task}
                        </span>
                      </span>
                    </button>

                    {/* Quick launch without opening the preview first */}
                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${tool.name} in a new tab`}
                      title="Open in new tab"
                      className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md text-[#9AA39D] transition-colors hover:bg-[#EDEAE0] hover:text-[#1E7F6C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E7F6C]"
                    >
                      <FaExternalLinkAlt aria-hidden="true" className="h-3 w-3" />
                    </a>
                  </div>
                );
              })}
            </section>
          )}
        </div>

        {/* Preview panel */}
        <section
          ref={previewRef}
          aria-live="polite"
          className={[
            "mt-6 scroll-mt-4 overflow-hidden border border-[#D6D2C4] bg-[#F7F6F0] transition-all duration-200 ease-out sm:mt-8",
            activeTool ? "opacity-100" : "max-h-0 border-0 opacity-0",
          ].join(" ")}
        >
          {activeTool && (
            <div>
              {/* Bezel / toolbar */}
              <div className="flex items-center justify-between gap-3 border-b border-[#D6D2C4] bg-[#1B2521] px-4 py-3 sm:px-5">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-7 w-7 flex-none items-center justify-center border border-[#3A4A44] text-sm text-[#7FCBB2]">
                    <activeTool.icon aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-serif text-sm text-[#F0EEE6] sm:text-base">
                      {activeTool.name}
                    </p>
                    <p className="truncate font-mono text-[10px] text-[#8A968F] sm:text-xs">
                      {activeTool.url.replace(/^https?:\/\//, "")}
                    </p>
                  </div>
                </div>
                <div className="flex flex-none items-center gap-2">
                  <a
                    href={activeTool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 border border-[#3A4A44] px-2.5 py-1.5 font-mono text-[11px] text-[#EAF4F0] transition-colors hover:border-[#7FCBB2] hover:text-[#7FCBB2] sm:text-xs"
                  >
                    <FaExternalLinkAlt aria-hidden="true" className="h-3 w-3" />
                    <span className="hidden sm:inline">Open in new tab</span>
                    <span className="sm:hidden">Open</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setActiveId(null)}
                    aria-label="Close preview"
                    className="flex h-7 w-7 items-center justify-center border border-[#3A4A44] text-[#EAF4F0] transition-colors hover:border-[#E2857A] hover:text-[#E2857A]"
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                </div>
              </div>

              {activeTool.embeddable ? (
                <div className="relative h-[65vh] min-h-[360px] w-full bg-[#0F1613] sm:h-[70vh] sm:min-h-[420px]">
                  {!loadedIds[activeTool.id] && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
                      <span className="h-5 w-5 animate-spin border-2 border-[#3A4A44] border-t-[#7FCBB2]" />
                      <p className="font-mono text-xs text-[#8A968F]">
                        Loading {activeTool.name}…
                      </p>
                    </div>
                  )}
                  <iframe
                    key={activeTool.id}
                    src={activeTool.url}
                    title={activeTool.name}
                    onLoad={() => handleIframeLoad(activeTool.id)}
                    className="h-full w-full border-0"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                  />
                </div>
              ) : (
                <div className="flex min-h-[260px] flex-col items-center justify-center gap-4 px-6 py-12 text-center sm:min-h-[320px] sm:py-14">
                  <span className="flex h-12 w-12 items-center justify-center border border-[#D6D2C4] text-lg text-[#1E7F6C]">
                    <FaLock aria-hidden="true" />
                  </span>
                  <div className="max-w-sm">
                    <p className="font-serif text-lg text-[#152420] sm:text-xl">
                      {activeTool.name} can&apos;t be previewed here
                    </p>
                    <p className="mt-2 font-serif text-sm text-[#4A5651] sm:text-base">
                      This tool blocks embedded previews. Open it in its own tab
                      to use it.
                    </p>
                  </div>
                  <a
                    href={activeTool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 flex w-full items-center justify-center gap-2 border border-[#1E7F6C] bg-[#1E7F6C] px-4 py-2.5 font-mono text-xs text-[#EAF4F0] transition-colors hover:bg-[#186559] sm:w-auto sm:text-sm"
                  >
                    <FaExternalLinkAlt aria-hidden="true" className="h-3 w-3" />
                    Open {activeTool.name}
                  </a>
                </div>
              )}
            </div>
          )}
        </section>

        {!activeTool && category.tools.length > 0 && (
          <p className="mt-6 font-mono text-xs text-[#6B776F] sm:mt-8 sm:text-sm">
            Select a tool above to preview it here, or use{" "}
            <FaExternalLinkAlt aria-hidden="true" className="inline h-2.5 w-2.5" /> to open it
            directly.
          </p>
        )}
      </div>
    </div>
  );
};

export default Tools;
